import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './people.css'

/*
 * peopleUI — the shared furniture of the people cluster.
 *
 * Users, Admin Users, Content Reports and a user's stats / challenges / posts
 * are eight screens that must read as one product, so the pieces they all need
 * live here once instead of eight times:
 *
 *   useTableStyles()  react-data-table-component styled from theme tokens
 *   Modal             token-styled dialog: Escape closes, full-bleed on phones
 *   Note              the inline replacement for alert()
 *   Confirm           the inline replacement for window.confirm() on destructive acts
 *   Empty / Skeleton  every list gets a real empty state and a real loading state
 *   Meta              a read-only block of label/value pairs
 *   Thumb             a poster thumbnail that opens the real player
 *   Icons             stroked, currentColor, so they follow the token palette
 *
 * Nothing in this file talks to the API.
 */

/* ============================================================
   ICONS
   ============================================================ */
const svg = (children, extra) => (
  <svg className={extra ? 'pp-i ' + extra : 'pp-i'} viewBox="0 0 24 24" aria-hidden="true">
    {children}
  </svg>
)

export const IconEdit = () => svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>)
export const IconX = () => svg(<path d="M18 6 6 18M6 6l12 12" />)
export const IconSearch = () => svg(<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></>)
export const IconPlus = () => svg(<path d="M12 5v14M5 12h14" />)
export const IconPlay = () => svg(<path d="M7 4.5v15l13-7.5-13-7.5Z" />)
export const IconOpen = () => svg(<><path d="M5 12h13" /><path d="m13 6 6 6-6 6" /></>)
export const IconAlert = () => svg(<><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>)
export const IconTrash = () => svg(<><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M10 11v6M14 11v6" /></>)
export const IconBan = () => svg(<><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></>)
export const IconBack = () => svg(<><path d="M19 12H6" /><path d="m11 18-6-6 6-6" /></>)
export const IconRefresh = () => svg(<><path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 4v5h-5" /></>)

/* ============================================================
   THEME TOKENS FOR react-data-table-component

   The table styles itself through a JS object, so it cannot read
   CSS custom properties on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference moves.
   ============================================================ */
const TOKEN_NAMES = [
  '--panel', '--panel-raise', '--line', '--line-soft',
  '--ink', '--ink-2', '--ink-3', '--accent', '--accent-wash',
  '--mono', '--sans', '--step-0'
]

const readTokens = () => {
  const out = {}
  if (typeof window === 'undefined' || !document.documentElement) return out
  const styles = window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach((name) => {
    out[name] = String(styles.getPropertyValue(name) || '').trim()
  })
  return out
}

export function useThemeTokens() {
  const [tokens, setTokens] = useState(readTokens)

  useEffect(() => {
    let frame = null
    const refresh = () => {
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame = window.requestAnimationFrame(() => setTokens(readTokens()))
    }

    const observer = new MutationObserver(refresh)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style']
    })

    let media = null
    if (window.matchMedia) {
      media = window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change', refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media) {
        if (media.removeEventListener) media.removeEventListener('change', refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  }, [])

  return tokens
}

/**
 * customStyles for <DataTable/>, rebuilt whenever the theme changes.
 * `dense` tightens the row height for the very long tables.
 */
export function useTableStyles(dense) {
  const tokens = useThemeTokens()
  const rowHeight = dense ? '46px' : '54px'

  return useMemo(() => {
    const t = tokens
    return {
      table: { style: { backgroundColor: t['--panel'], color: t['--ink'] } },
      tableWrapper: { style: { display: 'block' } },
      responsiveWrapper: { style: { borderRadius: 0 } },
      head: { style: { color: t['--ink-3'] } },
      headRow: {
        style: {
          backgroundColor: t['--panel-raise'],
          color: t['--ink-3'],
          minHeight: '40px',
          borderBottomWidth: '1px',
          borderBottomColor: t['--line'],
          borderBottomStyle: 'solid'
        }
      },
      headCells: {
        style: {
          paddingLeft: '12px',
          paddingRight: '12px',
          fontFamily: t['--mono'],
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: t['--ink-3']
        }
      },
      rows: {
        style: {
          backgroundColor: t['--panel'],
          color: t['--ink'],
          fontFamily: t['--sans'],
          fontSize: t['--step-0'] || '0.875rem',
          minHeight: rowHeight,
          '&:not(:last-of-type)': {
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: t['--line-soft']
          }
        },
        highlightOnHoverStyle: {
          backgroundColor: t['--accent-wash'],
          color: t['--ink'],
          borderBottomColor: t['--line-soft'],
          outlineWidth: '0px',
          outlineColor: t['--panel'],
          transitionDuration: '0.15s',
          transitionProperty: 'background-color'
        }
      },
      cells: { style: { paddingLeft: '12px', paddingRight: '12px' } },
      pagination: {
        style: {
          backgroundColor: t['--panel'],
          color: t['--ink-3'],
          fontSize: '12px',
          minHeight: '48px',
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderTopColor: t['--line']
        },
        pageButtonsStyle: {
          color: t['--ink-2'],
          fill: t['--ink-2'],
          backgroundColor: 'transparent',
          height: '40px',
          width: '40px',
          '&:disabled': { color: t['--ink-3'], fill: t['--ink-3'] },
          '&:hover:not(:disabled)': { backgroundColor: t['--panel-raise'] },
          '&:focus': { outline: 'none', backgroundColor: t['--accent-wash'] }
        }
      },
      noData: { style: { backgroundColor: t['--panel'], color: t['--ink-3'], padding: '0' } },
      progress: { style: { backgroundColor: t['--panel'], color: t['--ink-3'] } }
    }
  }, [tokens, rowHeight])
}

/* ============================================================
   INLINE NOTE — replaces alert()
   ============================================================ */
export function Note({ tone = 'info', children, onDismiss }) {
  if (!children) return null
  return (
    <div className="pp-note" data-tone={tone} role={tone === 'crit' ? 'alert' : 'status'}>
      <div className="pp-note-body">{children}</div>
      {onDismiss ? (
        <button type="button" className="pp-note-x" onClick={onDismiss} aria-label="Close">
          <IconX />
        </button>
      ) : null}
    </div>
  )
}

/* ============================================================
   EMPTY STATE
   ============================================================ */
export function Empty({ title, children, action }) {
  return (
    <div className="pp-empty">
      <div className="pp-empty-t">{title}</div>
      {children ? <div className="pp-empty-s">{children}</div> : null}
      {action ? <div className="pp-empty-a">{action}</div> : null}
    </div>
  )
}

/* ============================================================
   SKELETONS
   ============================================================ */
export function TableSkeleton({ rows = 6 }) {
  const bars = []
  for (let i = 0; i < rows; i += 1) bars.push(<span className="skeleton" key={i} />)
  return (
    <div className="pp-sk" aria-hidden="true">
      <span className="skeleton" />
      {bars}
    </div>
  )
}

export function LineSkeleton({ lines = 3 }) {
  const bars = []
  for (let i = 0; i < lines; i += 1) {
    bars.push(<span className={i === lines - 1 ? 'skeleton short' : 'skeleton'} key={i} />)
  }
  return <div className="pp-sk-inline" aria-hidden="true">{bars}</div>
}

/* ============================================================
   FORM FURNITURE

   Fields are written out inline on each screen — a <label> above its
   control, styled by .pp-label / .pp-input in people.css — because the
   ids have to be real and stable for the label to point at. Only the
   two pieces that carry logic live here.
   ============================================================ */

/** A read-only block of label/value pairs: the parts of a record the
    admin may see but not change. A dead disabled input invites a click
    that goes nowhere; text does not. */
export function Meta({ items }) {
  const list = (items || []).filter(Boolean)
  if (list.length === 0) return null
  return (
    <div className="pp-meta">
      {list.map(([k, v]) => (
        <div className="pp-meta-item" key={k}>
          <span className="pp-meta-k">{k}</span>
          <span className="pp-meta-v">{v === 0 ? '0' : v || ''}</span>
        </div>
      ))}
    </div>
  )
}

export function Pill({ tone, children }) {
  return <span className="pp-pill" data-tone={tone}>{children}</span>
}

/* ============================================================
   MODAL

   Escape closes, the scrim closes, focus lands inside on open and
   returns to the opener on close, and the page behind cannot scroll.
   Under 560px it goes full-bleed (see people.css).
   ============================================================ */
export function Modal({ open, title, subtitle, size, onClose, children, footer, labelledBy }) {
  const boxRef = useRef(null)
  const openerRef = useRef(null)

  /* onClose is a fresh arrow on every render of the calling screen. If the
     effect below depended on it, every keystroke in a field inside the dialog
     would tear the effect down and rebuild it — which restores focus to the
     opener and then re-focuses the dialog, i.e. focus would jump out of the
     input the admin is typing in. The effect therefore keys on `open` alone
     and reaches the current handler through a ref. */
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return undefined

    openerRef.current = document.activeElement

    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation()
        closeRef.current()
      }
    }
    document.addEventListener('keydown', onKey)

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    /* Move focus into the dialog so Escape and Tab belong to it. */
    const frame = window.requestAnimationFrame(() => {
      if (boxRef.current) boxRef.current.focus()
    })

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      window.cancelAnimationFrame(frame)
      const opener = openerRef.current
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus()
      }
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="pp-modal-wrap">
      <button type="button" className="pp-scrim" aria-label="Close" onClick={onClose} tabIndex={-1} />
      <div
        className="pp-modal"
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : (typeof title === 'string' ? title : undefined)}
        ref={boxRef}
        tabIndex={-1}
      >
        <div className="pp-modal-head">
          <div className="pp-modal-headline">
            <div className="pp-modal-title" id={labelledBy}>{title}</div>
            {subtitle ? <div className="pp-modal-sub">{subtitle}</div> : null}
          </div>
          <button type="button" className="pp-modal-x" onClick={onClose} aria-label="Close">
            <IconX />
          </button>
        </div>

        <div className="pp-modal-body">{children}</div>

        {footer ? <div className="pp-modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  )
}

/* ============================================================
   CONFIRM — replaces window.confirm() on destructive actions.

   It names, in words, exactly what is about to happen and to whom,
   and the confirming button carries the critical colour. Nothing
   destructive fires from a single click anywhere in this cluster.
   ============================================================ */
export function Confirm({ title, children, confirmLabel, busy, onConfirm, onCancel }) {
  return (
    <div className="pp-confirm" role="alertdialog" aria-label={title}>
      <div className="pp-confirm-t"><IconAlert /> {title}</div>
      <div className="pp-confirm-s">{children}</div>
      <div className="pp-actions">
        <button type="button" className="btn-ui" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-ui pp-crit-solid"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  )
}

/* ============================================================
   SEARCH BOX
   Filters rows that are already in memory. It never issues a
   request, so no API contract is touched by typing here.
   ============================================================ */
export function Search({ value, onChange, placeholder, label }) {
  return (
    <div className="pp-search">
      <IconSearch />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
      />
      {value ? (
        <button type="button" className="pp-search-x" onClick={() => onChange('')} aria-label="Clear">
          <IconX />
        </button>
      ) : null}
    </div>
  )
}

/* ============================================================
   THUMB — a poster frame that opens the real player.

   Lists used to mount a full video player per row. One <img> and
   a play badge say the same thing for a fraction of the weight,
   and the row is still one click from the clip.
   ============================================================ */
export function Thumb({ src, label, onClick, fallback = 'No media' }) {
  const [broken, setBroken] = useState(false)
  const url = typeof src === 'string' ? src.trim() : ''
  const show = url && !broken

  return (
    <button type="button" className="pp-thumb" onClick={onClick} title={label} aria-label={label}>
      {show ? (
        <img src={url} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
      ) : (
        <span className="pp-thumb-fallback">{fallback}</span>
      )}
      <span className="pp-thumb-badge" aria-hidden="true"><IconPlay /></span>
    </button>
  )
}

/* ============================================================
   SMALL HELPERS shared by the cluster
   ============================================================ */

/** Safe "2024-05-22T00:00:00Z" -> "2024-05-22"; never throws on null. */
export function dayOf(value) {
  if (!value) return ''
  return String(value).split('T')[0]
}

/** First non-empty string among the candidates — used where the API
    spells the same idea more than one way across endpoints. */
export function firstOf() {
  for (let i = 0; i < arguments.length; i += 1) {
    const v = arguments[i]
    if (v !== null && v !== undefined && String(v).trim() !== '') return v
  }
  return ''
}

/** "Maria" + "Papadopoulou" -> "Maria Papadopoulou"; blanks collapse. */
export function fullName(first, last) {
  return [first, last]
    .map((p) => (p === null || p === undefined ? '' : String(p).trim()))
    .filter(Boolean)
    .join(' ')
}

/** Everything a row can be searched by, lowercased once. */
export function haystack(values) {
  return values
    .map((v) => (v === null || v === undefined ? '' : String(v)))
    .join(' ')
    .toLowerCase()
}

/**
 * Latest-value ref.
 *
 * The column arrays in this cluster are memoised with no dependencies, so their
 * cell callbacks close over the FIRST render's variables forever. Any handler
 * that needs to look a row up in state must therefore read it through a ref,
 * or it will be searching the empty array the screen started with.
 */
export function useLatest(value) {
  const ref = useRef(value)
  ref.current = value
  return ref
}
